// src/components/VideoEngine/Templates/MSN_Landscape/Composition.js

import React, { useEffect, useState } from "react";
import { AbsoluteFill, Sequence, continueRender, delayRender, Audio, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { TransitionWrapper } from "../../Common/TransitionWrapper";
import { IntroSlide } from "./Sequences/Intro";
import { RankingSlide } from "./Sequences/RankingSlide";
import { OutroSlide } from "./Sequences/Outro";

// --- 1. SAFE FONT LOADER (Blocking Strategy) ---
// We use delayRender to pause the video engine until fonts are ready.
const loadFonts = () => {
  const handle = delayRender("Loading Google Fonts");

  const fontLink = document.createElement("link");
  fontLink.href = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@400;700;900&display=swap";
  fontLink.rel = "stylesheet";

  document.head.appendChild(fontLink);

  fontLink.onload = async () => {
    try {
      // THE BARRIER: Explicitly force the browser to download and parse these exact weights
      // before FFmpeg is allowed to snap Frame 0.
      await document.fonts.load("400 12px 'Outfit'");
      await document.fonts.load("700 12px 'Outfit'");
      await document.fonts.load("900 12px 'Outfit'");
      await document.fonts.load("400 12px 'Bebas Neue'");

      // Final safety net to ensure DOM application
      await document.fonts.ready;

      continueRender(handle);
    } catch (err) {
      console.warn("System: Font loading forced exit:", err);
      continueRender(handle); // Failsafe: Release the hold so the render doesn't hang indefinitely
    }
  };
};

// Helper: Film Grain Overlay
const FilmGrain = ({ opacity = 0.05 }) => (
  <AbsoluteFill style={{ pointerEvents: "none", opacity, zIndex: 99 }}>
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
      <filter id="noiseFilter">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.8"
          numOctaves="3"
          stitchTiles="stitch"
        />
      </filter>
      <rect width="100%" height="100%" filter="url(#noiseFilter)" />
    </svg>
  </AbsoluteFill>
);

// Helper: Unbreakable Brand Fallbacks (Node 4 Safety Net)
const FALLBACK_ASSETS = {
  'food': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Ffood.mp4?alt=media&token=9d93d720-dd76-491a-9531-2f429dbeda0e',
  'money': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fmoney.mp4?alt=media&token=1a616261-fce9-4bba-a4d5-9bb9e3b00f57',
  'exploring': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fexploring.mp4?alt=media&token=da42411b-f64c-4cb3-9fec-b06cf2031290',
  'property': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fproperty.mp4?alt=media&token=0546d926-7714-48f9-a6f3-30f70e3cc8bb',
  'default': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fdefault.mp4?alt=media&token=0dd09a7e-f14d-4926-9489-734d2ea68a05'
};

export const Composition = ({
  meta,
  theme,
  transitions,
  timeline: originalTimeline, // <--- ALIASED to intercept
  backgroundVideo,
  pexelsCredit,
  captionCadence = 1.2,       // <--- Director's Override
  captionStyle = 'general',    // <--- NEW: Director's Override
  musicTrack,                 // <--- NEW: Audio Engine
  generateAudio               // <--- NEW: Audio Engine Ducking Flag     // <--- Director's Override
}) => {
  // Trigger Font Loading ONCE on mount
  const [fontsLoaded, setFontsLoaded] = useState(false);
  useEffect(() => {
    loadFonts();
    // Small safety timeout to ensure state updates if fonts act weird
    const t = setTimeout(() => setFontsLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

    // --- NEW: NODE 4 BACKGROUND VERIFICATION ---
      // If the frontend sends an empty string, evaluate the payload brand and provide an ironclad fallback.
      
      const safeBackgroundVideo = backgroundVideo || FALLBACK_ASSETS[meta?.brand] || FALLBACK_ASSETS['default'];

  let currentStartFrame = 0;

  // --- NEW: AUDIO DYNAMICS (Fade Out) ---
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();

  // 1. Determine our ceiling
  const baseVolume = generateAudio ? 0.25 : 0.8;
  const fadeOutDuration = 60;

  // 2. Map the frame to a volume level
  const dynamicVolume = interpolate(
    frame,
    // [Start Fading Here, Hit Zero Here]
    [durationInFrames - fadeOutDuration, durationInFrames],
    // [Start at Base Volume, End at 0]
    [baseVolume, 0],
    // Crucial: Clamp prevents the volume from exceeding baseVolume before the fade starts
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // --- NEW: DATA TRANSFORMATION LAYER (Anchor & Stretch) ---
  const timeline = React.useMemo(() => {
    if (!originalTimeline) return [];

    return originalTimeline.map(item => {
      if (!item.captions || item.captions.length === 0) return item;

      // Establish the Anchor (the true start of the voiceover sequence)
      const anchorFrame = item.captions[0].startFrame;

      const retimedCaptions = item.captions.map(cap => {
        const relativeStart = cap.startFrame - anchorFrame;
        const duration = cap.endFrame - cap.startFrame;

        // Apply cadence multiplier ONLY to the relative offset and duration
        const newStart = anchorFrame + Math.floor(relativeStart * captionCadence);
        const newEnd = newStart + Math.floor(duration * captionCadence);

        return {
          ...cap,
          startFrame: newStart,
          endFrame: newEnd
        };
      });

      return { ...item, captions: retimedCaptions };
    });
  }, [originalTimeline, captionCadence]);

  // 1. EXTRACT IMAGES FOR CAROUSEL
  const teaserImages = timeline
    .filter(t => t.type === 'ranking_slide')
    .map(t => t.image || "")
    .slice(0, 4);

  // 2. INJECT TYPOGRAPHY & STYLE INTO THEME
  // We override the default fonts with our loaded Google Fonts
  const dynamicTheme = {
    ...theme,
    fontDisplay: "'Bebas Neue', sans-serif",
    fontMain: "'Outfit', sans-serif",
    highlightAll: captionStyle === 'social', // <--- NEW: Inject Style Flag for deep children
    captionStyle: captionStyle
  };

  return (
    <AbsoluteFill style={{ backgroundColor: "black" }}>
      <FilmGrain opacity={0.06} />

      {/* --- NEW: AUDIO ENGINE (Music Track & Ducking) --- */}
      {musicTrack && (
        <Audio
          src={musicTrack}
          volume={dynamicVolume}
          loop
        />
      )}

  

      {timeline.map((item, index) => {
        const startFrame = currentStartFrame;
        const duration = item.durationInFrames + transitions.overlap;

        // Advance the "head" for the next slide
        currentStartFrame += duration - transitions.overlap;

        const zIndex = index;

        return (
          <Sequence
            key={index}
            from={startFrame}
            durationInFrames={duration}
            style={{ zIndex }}
          >
            <TransitionWrapper
              type={transitions.type}
              overlap={transitions.overlap}
              isFirst={index === 0}
              theme={dynamicTheme} // <--- Pass the Font-Aware Theme
            >
              {item.type === "ranking_slide" ? (
                <RankingSlide
                  item={item}
                  theme={dynamicTheme}
                  duration={duration}
                  brand={meta?.brand} // <--- Bridging the brand data down
                />
              ) : item.type === "intro" ? (
                <IntroSlide 
                    item={item} 
                    theme={dynamicTheme} 
                    brand={meta.brand}
                    teaserImages={teaserImages} 
                    backgroundVideo={safeBackgroundVideo} // <--- Node 4 Guardrail Active
                />
              ) : (
                 <OutroSlide 
                    item={item} 
                    theme={dynamicTheme} 
                    brand={meta?.brand} 
                    backgroundVideo={safeBackgroundVideo} // <--- Node 4 Guardrail Active
                    pexelsCredit={pexelsCredit}
                 />
              )}
            </TransitionWrapper>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};